import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Navigation, Map, Phone, CheckCircle2, Calendar, DollarSign } from 'lucide-react';
import { authedAxios, wazeUrl, googleMapsUrl, formatJobTimeShort, formatJobDate } from './api';

const isSameDay = (a, b) => {
  const x = new Date(a), y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};

export default function TodayView() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await authedAxios().get('/admin/jobs/today');
      setJobs(data);
    } catch (e) {
      console.error('today load', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const complete = async (id) => {
    setCompleting((c) => ({ ...c, [id]: true }));
    try {
      await authedAxios().post(`/admin/jobs/${id}/complete`);
      await load();
    } catch (e) {
      alert('Failed to complete job');
    } finally {
      setCompleting((c) => ({ ...c, [id]: false }));
    }
  };

  const now = new Date();
  const today = jobs.filter((j) => isSameDay(j.scheduled_at, now));
  const upcoming = jobs.filter((j) => !isSameDay(j.scheduled_at, now));
  const todayRevenue = today.reduce((s, j) => s + (j.price || 0), 0);

  if (loading) return <div className="py-12 text-center text-gray-600">Loading jobs…</div>;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3" data-testid="today-summary">
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Today&apos;s jobs</p>
            <p className="text-2xl font-bold" data-testid="today-job-count">{today.length}</p>
          </div>
          <Calendar className="h-8 w-8 text-green-600" />
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Today&apos;s revenue</p>
            <p className="text-2xl font-bold" data-testid="today-revenue">${todayRevenue.toFixed(0)}</p>
          </div>
          <DollarSign className="h-8 w-8 text-green-600" />
        </CardContent></Card>
      </div>

      {/* Today's jobs */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Today</h2>
        {today.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-gray-500">No jobs today. Enjoy the day off 🌿</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {today.map((j) => (
              <JobCard key={j.id} job={j} onComplete={() => complete(j.id)} completing={!!completing[j.id]} highlight />
            ))}
          </div>
        )}
      </div>

      {/* Upcoming next 48h */}
      {upcoming.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Next 48h</h2>
          <div className="space-y-3">
            {upcoming.map((j) => (
              <JobCard key={j.id} job={j} onComplete={() => complete(j.id)} completing={!!completing[j.id]} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function JobCard({ job, onComplete, completing, highlight }) {
  return (
    <Card className={highlight ? 'border-green-500 border-2' : ''} data-testid={`job-card-${job.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate">{job.customer_name}</p>
            <p className="text-sm text-gray-600 truncate">{job.customer_address}</p>
          </div>
          <Badge className="bg-green-100 text-green-800 border-0 whitespace-nowrap" data-testid={`job-time-${job.id}`}>
            {highlight ? formatJobTimeShort(job.scheduled_at) : `${formatJobDate(job.scheduled_at)} ${formatJobTimeShort(job.scheduled_at)}`}
          </Badge>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{job.customer_phone}</span>
          <span className="font-bold text-green-700">${(job.price || 0).toFixed(0)}</span>
        </div>
        {job.notes && <p className="text-xs text-gray-500 bg-yellow-50 p-2 rounded">{job.notes}</p>}
        <div className="grid grid-cols-2 gap-2">
          <a href={wazeUrl(job.customer_address)} target="_blank" rel="noreferrer" data-testid={`job-waze-${job.id}`}>
            <Button className="w-full bg-cyan-500 hover:bg-cyan-600 text-white text-sm" type="button">
              <Navigation className="h-4 w-4 mr-1" /> Waze
            </Button>
          </a>
          <a href={googleMapsUrl(job.customer_address)} target="_blank" rel="noreferrer" data-testid={`job-maps-${job.id}`}>
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm" type="button">
              <Map className="h-4 w-4 mr-1" /> Maps
            </Button>
          </a>
          <a href={`tel:${job.customer_phone}`} data-testid={`job-call-${job.id}`}>
            <Button className="w-full bg-gray-800 hover:bg-gray-900 text-white text-sm" type="button">
              <Phone className="h-4 w-4 mr-1" /> Call
            </Button>
          </a>
          <Button
            onClick={onComplete}
            disabled={completing}
            className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
            data-testid={`job-complete-${job.id}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" /> {completing ? '…' : 'Done'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
